import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { Card } from '@/common/components/Card/Card';
import { Grid } from '@/common/components/Grid/Grid';
import { Heading } from '@/common/components/Heading/Heading';
import { Icon } from '@/common/components/Icons/Icon';
import { LabeledValue } from '@/common/components/LabeledValue/LabeledValue';
import { PageLayoutHorizontalScroll } from '@/common/components/PageLayout/PageLayout';
import { Section } from '@/common/components/Section/Section';
import { Stack } from '@/common/components/Stack/Stack';
import { Switch } from '@/common/components/Switch/Switch';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import {
  IInsuranceMember,
  IInsurancePolicy,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { PolicyPeopleDrawer } from './PolicyPeopleDrawer/PolicyPeopleDrawer';
import { StatusBadge } from './PolicyPeopleList.utils';

const removedStatuses = ['cancelled', 'removed', 'left'];

export const PolicyPeopleList = ({ policy }: TPolicyPeopleListProps) => {
  const { t } = useTranslation();
  const [selectedPerson, setSelectedPerson] = useState<IInsuranceMember>();
  const [showRemoved, setShowRemoved] = useState(false);

  const { coveredPeople, hasRemovedPeople } = useMemo(() => {
    const activePeople = policy.persons.filter(
      (person) => !removedStatuses.some((status) => person.status === status)
    );
    const removedPeople = policy.persons.filter((person) =>
      removedStatuses.some((status) => person.status === status)
    );

    return {
      coveredPeople: showRemoved
        ? activePeople.concat(removedPeople)
        : activePeople,
      hasRemovedPeople: removedPeople.length > 0,
    };
  }, [policy.persons, showRemoved]);

  return (
    <Stack gap="section">
      <Section
        title={t('dashboard.insurance_detail.table_headers.covered_people')}
        action={
          hasRemovedPeople && (
            <Switch
              variant="descriptive"
              label={t('dashboard.insurance_detail.removed_filter')}
              checked={showRemoved}
              onChange={(checked) => {
                setShowRemoved(checked);
              }}
            />
          )
        }
      >
        <PeopleCardsWrapper>
          {coveredPeople.map((person) => (
            <Card
              key={person.id}
              onPress={() => {
                setSelectedPerson(person);
              }}
              className="min-w-[22.4rem] max-w-[22.4rem] grow sm:max-w-full"
            >
              <Stack gap="sm" align="start" justify="between" className="grow">
                <Stack gap="lg" className="w-full">
                  <Stack
                    align="center"
                    gap="lg"
                    justify="between"
                    horizontal
                    className="w-full"
                  >
                    <Icon
                      icon="user"
                      color={
                        removedStatuses.includes(person.status)
                          ? 'neutral20'
                          : 'accentSecondary10'
                      }
                      size={20}
                    />
                    <StatusBadge person={person} />
                  </Stack>
                  <Heading level={4}>{person.shortName}</Heading>
                  {person.cardNumber && (
                    <LabeledValue
                      label={t('dashboard.insurance.card_number')}
                      value={person.cardNumber}
                    />
                  )}
                </Stack>
                <Button variant="tertiary" size="sm">
                  {t('dashboard.insurance_detail.see_more')}
                </Button>
              </Stack>
            </Card>
          ))}
        </PeopleCardsWrapper>
      </Section>
      {selectedPerson && (
        <PolicyPeopleDrawer
          policy={policy}
          person={selectedPerson}
          onClose={() => setSelectedPerson(undefined)}
        />
      )}
    </Stack>
  );
};

function PeopleCardsWrapper({ children }: { children: React.ReactNode }) {
  const breakpoints = useBreakpoints(['sm']);
  if (breakpoints.sm) {
    return (
      <Grid columnBasis="basis-[22.4rem]" gap="md" maxColumns={3}>
        {children}
      </Grid>
    );
  }

  return (
    <PageLayoutHorizontalScroll className="grow gap-md">
      {children}
    </PageLayoutHorizontalScroll>
  );
}

export type TPolicyPeopleListProps = {
  policy: IInsurancePolicy;
};
